/**
 * Fallback experiment ideas when Firecrawl generation is slow or failed.
 * Same card shape as PageAnalysisRecommendation so the plan step never empties.
 */
import type { PageAnalysisRecommendation } from "@/lib/api/onboardingTests"
import type { OnboardingGoalId } from "@/lib/types/onboarding-goal"
import type { PlatformId } from "@/lib/types/platform"

type RecSeed = Omit<PageAnalysisRecommendation, "id">

const BY_GOAL: Record<Exclude<OnboardingGoalId, "express">, RecSeed[]> = {
  saas: [
    {
      title: "Test the homepage signup headline",
      description: "Lead with the outcome a trial actually delivers, not the product category.",
      element_selector: "h1",
      element_type: "heading",
      change_type: "text",
      reasoning: "Homepage headline is the highest-traffic decision point for trial signups.",
      expected_impact: "More trial starts from the same traffic",
      priority: 100,
      implementation_effort: "low",
    },
    {
      title: "Clarify the primary CTA",
      description: "Swap a generic 'Get started' for a specific trial action.",
      element_selector: "a[href*='signup'], a[href*='trial'], button",
      element_type: "cta",
      change_type: "text",
      reasoning: "CTA copy that names the next step converts better than a vague start.",
      expected_impact: "Higher click-through into signup",
      priority: 90,
      implementation_effort: "low",
    },
  ],
  ecommerce: [
    {
      title: "Test add-to-cart copy on the product page",
      description: "Name the outcome (free shipping, in-stock, one-click) instead of a generic add.",
      element_selector: "[name='add'], button[type='submit'], .add-to-cart",
      element_type: "cta",
      change_type: "text",
      reasoning: "Product-page CTA is the highest-leverage ecommerce test with no deploy.",
      expected_impact: "More add-to-carts from the same product traffic",
      priority: 100,
      implementation_effort: "low",
    },
    {
      title: "Surface a trust line above the fold",
      description: "A one-line guarantee or social proof next to the buy button.",
      element_selector: ".product__info, .product-form, main",
      element_type: "block",
      change_type: "text",
      reasoning: "Hesitation at the buy button is usually trust, not price.",
      expected_impact: "Fewer product-page bounces before cart",
      priority: 85,
      implementation_effort: "low",
    },
  ],
  newsletter: [
    {
      title: "Test the subscribe headline",
      description: "Promise a specific issue they'll get, not 'join the newsletter'.",
      element_selector: "h1, h2",
      element_type: "heading",
      change_type: "text",
      reasoning: "Subscribe conversion is almost always a copy problem on the capture page.",
      expected_impact: "More subscribers from the same visitors",
      priority: 100,
      implementation_effort: "low",
    },
    {
      title: "Make the subscribe CTA specific",
      description: "Replace 'Subscribe' with the cadence or the first send.",
      element_selector: "button, input[type='submit'], a[href*='subscribe']",
      element_type: "cta",
      change_type: "text",
      reasoning: "Naming what they get next beats a generic subscribe verb.",
      expected_impact: "Higher form completion",
      priority: 90,
      implementation_effort: "low",
    },
  ],
  agency: [
    {
      title: "Test the client landing-page headline",
      description: "Lead with the result this page is supposed to sell, not the service category.",
      element_selector: "h1",
      element_type: "heading",
      change_type: "text",
      reasoning: "Client sites usually bury the offer. Headline tests are the fastest proof of lift.",
      expected_impact: "A result you can show the client this week",
      priority: 100,
      implementation_effort: "low",
    },
    {
      title: "Test the primary contact CTA",
      description: "Book / inquiry buttons that name the next step convert better than 'Contact us'.",
      element_selector: "a[href*='contact'], a[href*='book'], button",
      element_type: "cta",
      change_type: "text",
      reasoning: "Agencies need a visible conversion they can report, not vanity traffic.",
      expected_impact: "More qualified inquiries",
      priority: 90,
      implementation_effort: "low",
    },
  ],
}

const PLATFORM_TWIST: Partial<Record<PlatformId, Partial<RecSeed>>> = {
  shopify: {
    title: "Test the product-page add-to-cart label",
    element_selector: "form[action*='cart'] button, .product-form__submit",
  },
  beehiiv: {
    title: "Test the subscribe embed headline",
    element_selector: "[data-element='header'], h1",
  },
  kit: {
    title: "Test the subscribe form headline",
    element_selector: "h1, .formkit-header",
  },
  webflow: {
    title: "Test the hero heading on the home canvas",
    element_selector: "h1",
  },
}

export function heuristicRecommendations(
  goal: OnboardingGoalId | null | undefined,
  platform?: PlatformId | null,
): PageAnalysisRecommendation[] {
  const key = goal && goal !== "express" ? goal : "saas"
  const seeds = BY_GOAL[key].slice(0, 2)
  const twist = platform && platform !== "generic" ? PLATFORM_TWIST[platform] : undefined

  return seeds.map((seed, i) => {
    const merged = i === 0 && twist ? { ...seed, ...twist } : seed
    return {
      ...merged,
      id: `heuristic-${key}-${i + 1}`,
    }
  })
}

export function successLooksLike(
  goal: OnboardingGoalId | null | undefined,
  recTitle?: string,
): string {
  const noun =
    goal === "ecommerce"
      ? "orders"
      : goal === "newsletter"
        ? "subscribers"
        : goal === "agency"
          ? "client conversions"
          : "trials"
  const test = recTitle ? recTitle.replace(/^Test /i, "").toLowerCase() : "this first test"
  return `A win on ${test} looks like more ${noun} from the same traffic, measured in Humblytics, not a guess.`
}
